import React, { useState, useEffect } from "react";
import "../css/CustomerScreen.css";
import { AnimatePresence, motion } from "framer-motion";
import api from "../api"; // ✅ centralized API instance
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import CustomerCard from "../components/CustomerCard";
import FilterCompoent from "../components/FilterCompoent";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import PawAnimation from "../components/PawAnimation"; // ✅ Loading animation

export default function CustomerScreen() {
  const [customers, setCustomers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 🔹 Filters
  const [filters, setFilters] = useState({
    name: false,
    mobile: false,
    email: false,
    subscribed: false,
    unsubscribed: false,
  });

  const [inputValues, setInputValues] = useState({
    name: "",
    mobile: "",
    email: "",
  });

  // 🔹 Helper to get full name
  const getFullName = (customer) =>
    customer.fullName ||
    customer.name ||
    `${customer.first_name ?? ""} ${customer.last_name ?? ""}`;

  // 🔹 Fetch customers from API
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        setError("");

        // ✅ Correct API endpoint
        const response = await api.get("/customers/");
        setCustomers(response.data);
      } catch (err) {
        console.error("Failed to fetch customers:", err);
        setError("Failed to fetch customers. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  // --- Delete Modal Handlers ---
  const handleOpenDeleteModal = (id, name) => {
    setCustomerToDelete({ id: String(id), name });
    setIsModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setCustomerToDelete(null);
  };

  const handleConfirmDelete = async () => {
  if (customerToDelete) {
    try {
      // ✅ Call delete
      const response = await api.delete(`/customers/${customerToDelete.id}/`);
      console.log("Delete response:", response.data);

      // ✅ Remove customer from UI
      setCustomers((prev) =>
        prev.filter((c) => String(c.id) !== String(customerToDelete.id))
      );

      
    } catch (err) {
      console.error("Failed to delete customer:", err);
      alert("Failed to delete customer. Please try again.");
    }
  }
  handleCloseDeleteModal();
};


  // --- Status Toggle (Active / Inactive) ---
 // --- Status Toggle (Active / Inactive) ---
const handleStatusChange = async (id, newStatus) => {
  try {
    // ✅ Backend expects both user_id and status
    await api.patch(`/customers/ `, {
      user_id: id,
      status: newStatus,
    });

    // ✅ Update UI locally
    setCustomers((prev) =>
      prev.map((c) =>
        String(c.id) === String(id) ? { ...c, status: newStatus } : c
      )
    );

    // ✅ Toast feedback
    if (String(newStatus).toLowerCase() === "active") {
      toast.success("Customer is now Active");
    } else {
      toast.warning("Customer is now Inactive");
    }
  } catch (err) {
    console.error("Status update failed:", err);
    toast.error("Failed to update status.");
  }
};


  // --- Filter Handlers ---
  const handleFilterChange = (key, checked) => {
    setFilters((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleInputChange = (key, value) => {
    setInputValues((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (!filters[key] && value.trim() !== "") {
      setFilters((prev) => ({
        ...prev,
        [key]: true,
      }));
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const fullName = getFullName(customer);

    if (filters.name && inputValues.name.trim() !== "") {
      if (!fullName.toLowerCase().includes(inputValues.name.toLowerCase()))
        return false;
    }

    if (filters.mobile && inputValues.mobile.trim() !== "") {
      if (!customer.phone_number?.includes(inputValues.mobile)) return false;
    }

    if (filters.email && inputValues.email.trim() !== "") {
      if (
        !customer.email
          ?.toLowerCase()
          .includes(inputValues.email.toLowerCase())
      )
        return false;
    }

    if (filters.subscribed && !customer.subscribed) return false;
    if (filters.unsubscribed && customer.subscribed) return false;

    return true;
  });

  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />

        <div className="profile-settings">
          <div className="header-row">
            <h2 className="page-title">Customer</h2>

            <div className="filter-container">
              <FilterCompoent
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                filters={filters}
                inputValues={inputValues}
                onFilterChange={handleFilterChange}
                onInputChange={handleInputChange}
              />
            </div>
          </div>

          {/* --- Loading & Error UI --- */}
          {loading && (
            <div className="full-screen-loader">
              <PawAnimation />
            </div>
          )}
          {error && <p className="error">{error}</p>}

          {/* --- Customers Grid --- */}
          <div className="customer-grid">
            <AnimatePresence>
              {!loading &&
                !error &&
                filteredCustomers.map((customer) => {
                  const fullName = getFullName(customer);

                  return (
                    <motion.div
                      key={String(customer.id)}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CustomerCard
                        id={String(customer.id)} // normalize
                        profile_image={customer.profile_image || ""}
                        name={fullName}
                        petsCount={customer.petsCount || 0}
                        phone={customer.phone_number || ""}
                        email={customer.email || ""}
                        subscribed={customer.subscribed}
                        status={customer.status} // ✅ from backend
                        onDelete={() =>
                          handleOpenDeleteModal(customer.id, fullName)
                        }
                        onStatusChange={handleStatusChange} // ✅ pass handler
                      />
                    </motion.div>
                  );
                })}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* --- Delete Confirmation Modal --- */}
      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete ${customerToDelete?.name}?`}
        message="Are you sure you want to delete this customer? This action cannot be undone."
      />
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}
